/**
 * Wrapping of rendered notification HTML in the shared Unify email layout.
 *
 * Template files are body fragments, but templates stored before the shared layout (or authored in
 * Settings) may be full documents. Document-level tags are stripped first so the layout is never nested.
 * HTML that already is a wrapped Unify document (marker on its `<body>`) is returned unchanged.
 */
import { buildEmailLayout, hasEmailLayoutMarker, type EmailBranding } from '@unify/fhir-model/email-layout';
import { sanitizeHtml } from './notification-html-sanitizer';

/**
 * Rest of a tag up to its closing `>`, quote-aware: a `>` inside a quoted attribute value does not end the tag. The
 * three alternatives start with disjoint characters, so matching stays linear (an unterminated quote simply fails).
 */
const TAG_REST = `(?:[^<>"']|"[^"]*"|'[^']*')*`;
const DOCTYPE = new RegExp(`<!doctype${TAG_REST}>`, 'gi');
const TITLE_CLOSED = new RegExp(`<title\\b${TAG_REST}>[^<]*<\\/title\\s*>`, 'gi');
/** An unclosed <title>: drop it and its text up to the next tag. */
const TITLE_OPEN = new RegExp(`<title\\b${TAG_REST}>[^<]*`, 'gi');
const DOCUMENT_TAGS = new RegExp(`<\\/?(?:html|head|body)\\b${TAG_REST}>`, 'gi');

/**
 * Remove every <head> block; an unclosed <head> is dropped up to the next <body>, or to the end. Single pass over a
 * once-lowercased copy, so it is linear. `<header>` and `</header>` are not `<head>` / `</head>`.
 */
function stripHeadBlocks(html: string): string {
  // ASCII-only lowercase keeps indexes aligned with `html` (String#toLowerCase changes the length of e.g. U+0130).
  const lower = html.replace(/[A-Z]/g, (ch) => ch.toLowerCase());
  const isNameChar = (c: number): boolean => (c >= 97 && c <= 122) || (c >= 48 && c <= 57);
  const chunks: string[] = [];
  // Once one <head> has no real `</head` after it, no later one does either: stop rescanning to the end each time.
  let noRealClose = false;
  let pos = 0;
  let idx = lower.indexOf('<head', pos);
  while (idx !== -1) {
    if (isNameChar(lower.charCodeAt(idx + 5))) {
      idx = lower.indexOf('<head', idx + 5); // e.g. <header>
      continue;
    }
    chunks.push(html.slice(pos, idx));
    // Find the real </head (not </header).
    let end = -1;
    for (let at = noRealClose ? -1 : lower.indexOf('</head', idx); at !== -1; at = lower.indexOf('</head', at + 1)) {
      if (!isNameChar(lower.charCodeAt(at + 6))) {
        const gt = lower.indexOf('>', at);
        end = gt === -1 ? html.length : gt + 1;
        break;
      }
    }
    if (end === -1) noRealClose = true;
    if (end === -1) {
      const body = lower.indexOf('<body', idx);
      end = body === -1 ? html.length : body;
    }
    pos = Math.max(end, idx + 1); // always progress
    idx = lower.indexOf('<head', pos);
  }
  chunks.push(html.slice(pos));
  return chunks.join(' ');
}

/** Remove <!doctype>, <html>, <head> (with its contents), <title> and <body> wrappers; keep body content. */
export function stripDocumentWrapper(html: string): string {
  // Each removed tag leaves a space, never nothing: gluing its neighbours together (`java<html>script:`) could
  // rebuild a URL scheme, event handler or tag the sanitizer had already passed over.
  return stripHeadBlocks(html.replace(DOCTYPE, ' '))
    .replace(TITLE_CLOSED, ' ')
    .replace(TITLE_OPEN, ' ')
    .replace(DOCUMENT_TAGS, ' ')
    .trim();
}

/**
 * Sanitize rendered notification HTML, strip any document wrapper, then wrap it in the shared layout. Sanitizing here
 * means no caller can wrap unsanitized HTML; the strip leaves a space where a tag was, so it cannot splice a new one. Empty
 * input stays empty, and a document that already carries the layout marker is returned sanitized but not wrapped twice (sanitizing drops the layout's own <meta> tags, which no caller relies on).
 */
export function wrapEmailHtml(renderedHtml: string, branding?: EmailBranding): string {
  const sanitized = sanitizeHtml(renderedHtml);
  if (hasEmailLayoutMarker(sanitized)) return sanitized;
  // Stripping can join the halves of a tag it was inside (`<a href="x"<body>onclick=...>`): sanitize its result again.
  const stripped = sanitizeHtml(stripDocumentWrapper(sanitized));
  // An unterminated trailing tag (a `<` after the last `>`) would swallow the layout markup that follows it: escape
  // every `<` in that tail so it renders as text.
  const tailStart = stripped.lastIndexOf('>') + 1;
  const body = stripped.slice(0, tailStart) + stripped.slice(tailStart).replace(/</g, '&lt;');
  return body ? buildEmailLayout({ bodyHtml: body, branding }) : '';
}
